import { create } from 'zustand';
import { ANALYTICS_EVENTS, type UserDto, type Locale } from '@neke/core';
import { api, refresh, setAccessToken, setSessionExpiredHandler } from '../api/client';
import i18n, { persistLocale } from '../i18n';
import { identifyUser, resetUser, setUserLocale, track } from '../analytics';

interface SessionState {
  user: UserDto | null;
  ready: boolean;
  locale: Locale;
  restore: () => Promise<void>;
  setUser: (user: UserDto | null) => void;
  setLocale: (locale: Locale) => void;
  logout: () => Promise<void>;
}

/**
 * Восстановление сессии — событие одноразовое, на всю жизнь страницы. Промис
 * держим здесь: в StrictMode эффект прогоняется дважды, и второй заход не
 * должен превращаться во второй поход за токеном — сервер ротирует
 * refresh-куку и повтор с уже отозванной считает кражей.
 */
let restoring: Promise<void> | null = null;

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  ready: false,
  locale: (i18n.language as Locale) ?? 'kk',

  /** На старте пробуем поднять сессию по refresh-куке — без неё это просто аноним. */
  restore: () => {
    // Уже восстановились — второй раз незачем: дальше сессию ведут вход и выход.
    if (get().ready) return Promise.resolve();
    restoring ??= (async () => {
      try {
        const data = await refresh();
        set({ user: data.user, ready: true });
        identifyUser(data.user);
        if (data.user.locale !== get().locale) {
          get().setLocale(data.user.locale);
        }
      } catch {
        setAccessToken(null);
        set({ user: null, ready: true });
      } finally {
        restoring = null;
      }
    })();
    return restoring;
  },

  setUser: (user) => {
    set({ user });
    if (user) identifyUser(user);
  },

  setLocale: (locale) => {
    void i18n.changeLanguage(locale);
    persistLocale(locale);
    set({ locale });
    setUserLocale(locale);
    // Язык залогиненного пользователя запоминаем на сервере, но молча:
    // неудача здесь не должна мешать переключению интерфейса.
    if (get().user) void api.updateMe({ locale }).catch(() => undefined);
  },

  logout: async () => {
    await api.logout().catch(() => undefined);
    set({ user: null });
    await track(ANALYTICS_EVENTS.signedOut);
    // Сброс после события: иначе выход припишется уже новому анониму.
    resetUser();
  },
}));

/**
 * Сервер отказал по согласию (403 `consent_required`), а у нас в сторе
 * `hasConsent` стоит true — представление разошлось с базой. Чиним его перед
 * уходом на онбординг: иначе тот решит, что подписывать нечего, галочку
 * поставит сам, POST /api/me/consent не отправит — и человек вернётся на тот же
 * отказ.
 */
export function markConsentMissing(): void {
  const { user } = useSession.getState();
  if (user?.hasConsent) useSession.setState({ user: { ...user, hasConsent: false } });
}

/**
 * Обновиться не вышло — сессии конец. Убираем пользователя: страницы, которые
 * требуют входа, сами уведут на /login, а не останутся висеть на ошибке.
 *
 * Сервер здесь не трогаем: refresh уже мёртв, и повторный поход с ним сочтут
 * кражей токена. Отзыв — дело явного выхода, см. logout().
 */
setSessionExpiredHandler(() => {
  // Аноним на старте тоже получает отказ на refresh — там хоронить нечего.
  if (!useSession.getState().user) return;
  useSession.setState({ user: null, ready: true });
  resetUser();
});
